// Graupner Butler B-3 – Fehlerakten (BUT-0001 …) mit Status-Kette und Wiedervorlage. Nur Admin.
// Die Akten liegen in module_feedback (Typ „Bug“) und erscheinen dort ebenfalls; hier wird die
// Butler-Statuskette gepflegt: Offen → In Untersuchung → Reparatur vorbereitet → Behoben → Überprüft → Erledigt.
// BUT-0002/0003 (15.10.2026): „An Claude senden“ (Teilen-Menü) neben dem Kopieren; Endstatus „Verworfen“
// statt Löschen (nur aus Offen/In Untersuchung, Begründung Pflicht); verworfene Akten standardmäßig ausgeblendet.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { teilenMoeglich, berichtVorabLaden, anClaudeSenden } from "@/lib/butlerTeilen";

const STATUS = {
  offen: { label: "Offen", farbe: "bg-blue-100 text-blue-800" },
  in_untersuchung: { label: "In Untersuchung", farbe: "bg-amber-100 text-amber-800" },
  reparatur_vorbereitet: { label: "Reparatur vorbereitet", farbe: "bg-orange-100 text-orange-800" },
  behoben: { label: "Behoben", farbe: "bg-teal-100 text-teal-800" },
  ueberprueft: { label: "Überprüft", farbe: "bg-emerald-100 text-emerald-800" },
  erledigt: { label: "Erledigt", farbe: "bg-slate-200 text-slate-700" },
  nicht_reproduzierbar: { label: "Nicht reproduzierbar", farbe: "bg-purple-100 text-purple-800" },
  verworfen: { label: "Verworfen", farbe: "bg-zinc-200 text-zinc-600 line-through" },
};
const KETTE = ["offen", "in_untersuchung", "reparatur_vorbereitet", "behoben", "ueberprueft", "erledigt"];

const datum = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }); } catch { return iso || "—"; } };
const Marke = ({ s }) => <span className={`rounded px-2 py-0.5 text-xs font-semibold ${STATUS[s]?.farbe || ""}`}>{STATUS[s]?.label || s}</span>;

function AkteDetail({ aktenzeichen, onGeaendert }) {
  const [akte, setAkte] = useState(null);
  const [bemerkung, setBemerkung] = useState("");
  const [bericht, setBericht] = useState(null); // B-5 Variante A: Text zum Kopieren
  const [teilenText, setTeilenText] = useState(null); // vorab geladen für „An Claude senden“ (BUT-0002)
  const bemerkungRef = useRef(null);
  const laden = useCallback(() => api.get(`/butler/fehler/${aktenzeichen}`).then((r) => setAkte(r.data)).catch(() => toast.error("Akte nicht ladbar")), [aktenzeichen]);
  useEffect(() => { laden(); }, [laden]);
  useEffect(() => {
    setTeilenText(null);
    if (teilenMoeglich()) berichtVorabLaden(aktenzeichen).then(setTeilenText).catch(() => {});
  }, [aktenzeichen]);
  if (!akte) return <p className="p-4 text-slate-500">Lade …</p>;
  const b = akte.butler;

  const status = async (neu) => {
    if (neu === "verworfen" && !bemerkung.trim()) {
      toast.error("Zum Verwerfen bitte eine Begründung eintragen");
      bemerkungRef.current?.focus();
      return;
    }
    try {
      await api.post(`/butler/fehler/${aktenzeichen}/status`, { status: neu, bemerkung });
      setBemerkung(""); await laden(); onGeaendert();
    } catch (e) { toast.error(e?.response?.data?.detail || "Statuswechsel fehlgeschlagen"); }
  };
  const wiedervorlage = async (am) => {
    try { await api.patch(`/butler/fehler/${aktenzeichen}/wiedervorlage`, { am: am || null }); await laden(); onGeaendert(); }
    catch { toast.error("Wiedervorlage nicht gespeichert"); }
  };
  // B-5 Variante A „Spock hinzuziehen“: Bericht nur erzeugen und in die Zwischenablage legen – keine KI-Schnittstelle.
  const berichtErstellen = async () => {
    try {
      const r = await api.post(`/butler/fehler/${aktenzeichen}/bericht`);
      setBericht(r.data.text); laden();
      try { await navigator.clipboard.writeText(r.data.text); toast.success("Fehlerbericht kopiert", { description: "Jetzt in den Chat mit Spock einfügen." }); }
      catch { toast.info("Bitte den Text unten markieren und kopieren."); }
    } catch { toast.error("Fehlerbericht nicht erstellt"); }
  };
  const anClaude = async () => {
    try {
      if ((await anClaudeSenden(aktenzeichen, teilenText)) === "geteilt") { toast.success("An das Teilen-Menü übergeben"); laden(); }
    } catch { toast.error("Teilen nicht möglich – bitte „Bericht für Spock kopieren“ verwenden"); }
  };
  const rolle = Object.fromEntries((b.vorgaenge || []).map((v) => [v.vorgang_id, v.rolle]));

  return (
    <div className="space-y-4 p-4 text-sm">
      <div>
        <h2 className="text-lg font-bold">{akte.title}</h2>
        <p className="text-slate-500">angelegt {datum(akte.created_at)} von {akte.created_by} · Seite {b.seite || "—"} · Version Browser {b.version_client || "—"} / Server {b.version_server || "—"}</p>
        {akte.description && <p className="mt-2 whitespace-pre-wrap">{akte.description}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {KETTE.map((s, i) => (
          <span key={s} className="flex items-center gap-1">
            <span className={`rounded px-2 py-0.5 text-xs ${b.status === s ? STATUS[s].farbe + " font-bold ring-2 ring-slate-400" : "text-slate-400"}`}>{STATUS[s].label}</span>
            {i < KETTE.length - 1 && <span className="text-slate-300">→</span>}
          </span>
        ))}
        {(b.status === "nicht_reproduzierbar" || b.status === "verworfen") && <Marke s={b.status} />}
      </div>

      {akte.erlaubte_status.length > 0 && (
        <div className="space-y-2 rounded border p-3">
          <input ref={bemerkungRef} className="w-full rounded border px-2 py-1" placeholder={akte.erlaubte_status.includes("verworfen") ? "Bemerkung (beim Verwerfen Pflicht: Begründung)" : "Bemerkung zum Statuswechsel (optional)"} value={bemerkung} maxLength={1000} onChange={(e) => setBemerkung(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            {akte.erlaubte_status.map((s) => (
              <button key={s} type="button" onClick={() => status(s)} data-testid={`btn-status-${s}`}
                className={`rounded border px-3 py-1 ${s === "verworfen" ? "ml-auto border-zinc-300 text-zinc-600 hover:bg-zinc-100" : "hover:bg-slate-50"}`}>→ {STATUS[s].label}</button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2 rounded border border-indigo-200 bg-indigo-50 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={berichtErstellen} className="rounded bg-indigo-700 px-3 py-1 font-semibold text-white hover:bg-indigo-800">
            Bericht für Spock kopieren
          </button>
          {teilenMoeglich() && (
            <button type="button" onClick={anClaude} disabled={!teilenText} data-testid="btn-an-claude-senden"
              className="rounded border border-indigo-700 bg-white px-3 py-1 font-semibold text-indigo-800 hover:bg-indigo-100 disabled:opacity-50">
              {teilenText ? "An Claude senden" : "Bericht wird vorbereitet …"}
            </button>
          )}
        </div>
        <span className="ml-2 text-xs text-slate-600">Text zum Einfügen in den Chat – ohne Passwörter, Kundendaten und Inhalte.</span>
        {bericht && (
          <textarea readOnly rows={12} value={bericht} onFocus={(e) => e.target.select()}
            className="w-full rounded border bg-white p-2 font-mono text-xs" />
        )}
      </div>

      <label className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Wiedervorlage</span>
        <input type="date" className="rounded border px-2 py-1" value={b.wiedervorlage_am || ""} onChange={(e) => wiedervorlage(e.target.value)} />
        {b.wiedervorlage_am && <button type="button" className="text-xs underline" onClick={() => wiedervorlage(null)}>entfernen</button>}
      </label>

      <div>
        <h3 className="font-semibold">Markierte Vorgänge · Wiederholungen: {b.wiederholungen?.fehler || 0}× mit Fehler, {b.wiederholungen?.ohne_fehler || 0}× ohne</h3>
        {!(b.ablauf_kopie || []).length && <p className="text-slate-500">Kein Ablauf gesichert.</p>}
        <ul className="mt-1 divide-y rounded border">
          {(b.ablauf_kopie || []).map((v) => (
            <li key={v.vorgang_id} className={`px-2 py-1 ${v.eltern_id ? "pl-6" : ""}`}>
              <span className="font-medium">{v.name || v.vorgang_id}</span>
              {rolle[v.vorgang_id] && <span className={`ml-2 text-xs ${rolle[v.vorgang_id] === "fehler" ? "text-red-700" : "text-emerald-700"}`}>{rolle[v.vorgang_id] === "fehler" ? "● Fehler" : "○ ohne Fehler"}</span>}
              <span className="ml-2 text-xs text-slate-500">{datum(v.beginn)} · {v.ergebnis} · {v.anfragen} Anfragen{v.fehler ? `, ${v.fehler} mit Fehler` : ""}{v.schreibend?.length ? ` · schreibt: ${v.schreibend.join(", ")}` : ""}</span>
              {v.aktionen?.length > 0 && <div className="text-xs text-slate-600">Aktionen: {v.aktionen.join(" → ")}</div>}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="font-semibold">Verlauf</h3>
        <ul className="mt-1 space-y-1">
          {(akte.verlauf || []).map((h) => (
            <li key={h.id} className="text-xs"><span className="text-slate-500">{datum(h.created_at)} · {h.created_by}:</span> {h.type === "kommentar" ? <em>{h.text}</em> : h.text}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function FehlerAktenPage() {
  const [akten, setAkten] = useState([]);
  const [faellig, setFaellig] = useState([]);
  const [filter, setFilter] = useState("standard"); // Standard: ohne verworfene Akten
  const [auswahl, setAuswahl] = useState(null);

  const laden = useCallback(async () => {
    try {
      const [a, w] = await Promise.all([api.get("/butler/fehler", { params: { status: filter } }), api.get("/butler/wiedervorlage")]);
      setAkten(a.data || []); setFaellig(w.data?.faellig || []);
    } catch (e) { toast.error(e?.response?.status === 403 ? "Nur für Admin" : "Fehlerakten nicht ladbar"); }
  }, [filter]);
  useEffect(() => { laden(); }, [laden]);

  return (
    <div className="mx-auto max-w-6xl p-4">
      <h1 className="mb-3 text-2xl font-bold">Butler – Fehlerakten</h1>

      {faellig.length > 0 && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm">
          <strong>Wiedervorlage fällig ({faellig.length}):</strong>
          <ul className="mt-1">
            {faellig.map((a) => (
              <li key={a.id}>
                <button type="button" className="underline" onClick={() => setAuswahl(a.butler.aktenzeichen)}>{a.title}</button>
                <span className="ml-2 text-slate-600">seit {a.butler.wiedervorlage_am}</span> <Marke s={a.butler.status} />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-2 flex items-center gap-2 text-sm">
        <span>Status:</span>
        <select className="rounded border px-2 py-1" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="standard">alle ohne Verworfene</option>
          <option value="alle">alle mit Verworfenen</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="grid gap-4 grid-cols-[minmax(0,1fr)] md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">{/* Spalte am Handy nicht breiter als der Bildschirm */}
        <ul className="divide-y rounded border text-sm">
          {!akten.length && <li className="p-3 text-slate-500">Keine Fehlerakten.</li>}
          {akten.map((a) => (
            <li key={a.id}>
              <button type="button" onClick={() => setAuswahl(a.butler.aktenzeichen)}
                className={`w-full px-3 py-2 text-left hover:bg-slate-50 ${auswahl === a.butler.aktenzeichen ? "bg-slate-100" : ""}`}>
                <div className="font-medium">{a.title}</div>
                <div className="flex items-center gap-2 text-xs text-slate-500"><Marke s={a.butler.status} /> {datum(a.created_at)} · {(a.butler.vorgaenge || []).length} Vorgänge</div>
              </button>
            </li>
          ))}
        </ul>
        <div className="rounded border">
          {auswahl ? <AkteDetail key={auswahl} aktenzeichen={auswahl} onGeaendert={laden} /> : <p className="p-4 text-slate-500">Akte links auswählen.</p>}
        </div>
      </div>
    </div>
  );
}
